import { useContext, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Link,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Close } from "@mui/icons-material";
import { parseISO, format, max } from "date-fns";
import { sortBy } from "lodash";
import { StaffWorkLogsContext } from "../StaffWorkLogsDetail";
import {
    groupHoursByWeek,
    getEffectiveCumulativeToDate,
    resolveCumulativeFromDate,
    buildRunAwareWeeklyRows,
    hasValidDayOffConfig,
} from "../utils/calculations";
import { getWeekStartDate } from "../../../utils/dateUtils";
import { buildContractRuns, getFirstRunStartDate, filterTimeOffForBag } from "../utils/employmentRun";
import { formatDisplayHours, formatSaldo } from "../utils/general";
import { getLatestSnapshot, getEffectivePriorBalance } from "../utils/snapshots";
import { getLiveWindowStart } from "../utils/constants";
import { SnapshotManager } from "./SnapshotManager";
import { useAgreementTypeLabels } from "../utils/useAgreementTypeLabels";

type CumulativeHoursModalProps = {
    open: boolean;
    onClose: () => void;
};

const formatWeekLabel = (weekStartDate: Date, weekEndDate: Date) => {
    const label = `Semana ${format(weekStartDate, "dd/MM")} a ${format(weekEndDate, "dd/MM")}`;
    return label;
};

export const CumulativeHoursModal = ({ open, onClose }: CumulativeHoursModalProps) => {
    const { workLogs, timeOff, staffAgreement, extraHoursPaidItems, selectedYear, selectedMonth, publicHolidaysSet, priorBalance, cumulativeWindowStart, userSnapshots, isCurrentUserAdmin, userId, agreementTypeMissing } =
        useContext(StaffWorkLogsContext);

    const weeklyWorkHoursSchedule = useMemo(
        () => sortBy(staffAgreement?.staff_weekly_hours || [], (entry: any) => entry.date_start),
        [staffAgreement?.staff_weekly_hours]
    );

    const selectedMonthDate = new Date(selectedYear, selectedMonth);
    // The modal lists the WHOLE history since the window floor / first run — every relationship, each
    // segment with its own saldo — while the headline stays scoped to the current run.
    const anchorDate = getFirstRunStartDate(weeklyWorkHoursSchedule);
    const fromDate = resolveCumulativeFromDate(cumulativeWindowStart, anchorDate);
    const toDate = getEffectiveCumulativeToDate(selectedMonthDate, weeklyWorkHoursSchedule);
    const dayOffConfigValid = hasValidDayOffConfig(weeklyWorkHoursSchedule, fromDate, toDate) && !agreementTypeMissing;
    const runs = useMemo(() => buildContractRuns(weeklyWorkHoursSchedule), [weeklyWorkHoursSchedule]);
    const agreementTypeLabels = useAgreementTypeLabels();
    const bagTimeOff = useMemo(() => filterTimeOffForBag(timeOff, weeklyWorkHoursSchedule), [timeOff, weeklyWorkHoursSchedule]);

    const latestSnapshot = getLatestSnapshot(userSnapshots);
    const priorSnapshot = latestSnapshot ? getLatestSnapshot(userSnapshots.filter((snap) => snap !== latestSnapshot)) : null;
    const recheckWindowStart = getLiveWindowStart(priorSnapshot ? parseISO(priorSnapshot.checkpoint_date) : null);
    const priorBalanceForRecheck = getEffectivePriorBalance(priorSnapshot);
    const snapshotCheckpoint = latestSnapshot ? parseISO(latestSnapshot.checkpoint_date) : null;

    const weeklyBreakdown = useMemo(() => {
        const breakdown = groupHoursByWeek(workLogs, bagTimeOff, weeklyWorkHoursSchedule, fromDate, toDate);
        return breakdown;
    }, [workLogs, bagTimeOff, weeklyWorkHoursSchedule, fromDate, toDate]);

    // Extra hours paid is a monthly figure; bucket it into the week holding that month's first in-window
    // day. Months settled before the window (a previous relationship) are excluded, matching the headline.
    const extraHoursPaidByWeek = useMemo(() => {
        const map = new Map<string, number>();
        extraHoursPaidItems.forEach((item) => {
            const monthFirstDay = new Date(item.year, item.month - 1, 1);
            const monthLastDay = new Date(item.year, item.month, 0);
            if (monthLastDay < fromDate) return;
            const bucketDate = max([monthFirstDay, fromDate]);
            const weekKey = format(getWeekStartDate(bucketDate), "yyyy-MM-dd");
            map.set(weekKey, (map.get(weekKey) || 0) + (item.extra_hours_paid || 0));
        });
        return map;
    }, [extraHoursPaidItems, fromDate]);

    const rows = useMemo(() => {
        if (!dayOffConfigValid) return [];
        const result = buildRunAwareWeeklyRows({
            weeklyBreakdown,
            extraHoursPaidByWeek,
            weeklyWorkHoursSchedule,
            publicHolidaysSet,
            priorBalance,
            runs,
            agreementTypeLabels,
        });
        return result;
    }, [weeklyBreakdown, extraHoursPaidByWeek, weeklyWorkHoursSchedule, dayOffConfigValid, publicHolidaysSet, priorBalance, runs, agreementTypeLabels]);

    // The Total row covers the CURRENT segment only, so it keeps matching the headline saldo.
    const currentSegmentRows = useMemo(() => {
        const lastResetIndex = rows.reduce((lastIndex, row, rowIndex) => (row.isRunStart ? rowIndex : lastIndex), 0);
        const segmentRows = rows.slice(lastResetIndex);
        return segmentRows;
    }, [rows]);

    const grandTotal = useMemo(() => {
        const totals = currentSegmentRows.reduce(
            (acc, row) => ({
                scheduleHours: acc.scheduleHours + row.scheduleHours,
                holidayHours: acc.holidayHours + row.holidayHours,
                sickLeaveHours: acc.sickLeaveHours + row.sickLeaveHours,
                missedHours: acc.missedHours + row.missedHours,
                totalHours: acc.totalHours + row.totalHours,
                extraPaid: acc.extraPaid + row.extraPaid,
                consideredHours: acc.consideredHours + row.consideredHours,
                expectedHours: acc.expectedHours + row.expectedHours,
            }),
            { scheduleHours: 0, holidayHours: 0, sickLeaveHours: 0, missedHours: 0, totalHours: 0, extraPaid: 0, consideredHours: 0, expectedHours: 0 },
        );
        return totals;
    }, [currentSegmentRows]);

    const saldoAtual = rows.length > 0 ? rows[rows.length - 1].runningSaldo : priorBalance;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">Detalhe semanal de horas consideradas</Typography>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <TableContainer sx={{ maxHeight: "65vh" }}>
                    <Table size="small" stickyHeader sx={{ "& thead .MuiTableCell-root": { backgroundColor: "background.paper" } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Semana</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell align="right">Horário</TableCell>
                                <TableCell align="right">Férias</TableCell>
                                <TableCell align="right">Baixa</TableCell>
                                <TableCell align="right">Faltas</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                                    Total
                                </TableCell>
                                <TableCell align="right">Extra pagas</TableCell>
                                <TableCell align="right">Consideradas</TableCell>
                                <TableCell align="right">Esperadas</TableCell>
                                <TableCell align="right">Saldo sem.</TableCell>
                                <TableCell align="right">Saldo acum.</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {priorBalance !== 0 && (
                                <TableRow>
                                    <TableCell colSpan={11}>De anos anteriores</TableCell>
                                    <TableCell align="right">{formatSaldo(priorBalance)}</TableCell>
                                </TableRow>
                            )}
                            {rows.map((row, index) => (
                                <TableRow
                                    key={row.weekStart}
                                    sx={{
                                        backgroundColor: index % 2 === 1 ? "action.hover" : "inherit",
                                        ...(row.isRunStart ? { "& td": { borderTop: 2, borderTopColor: "primary.main" } } : {}),
                                    }}
                                >
                                    <TableCell>
                                        <Link
                                            component={RouterLink}
                                            to={`/staff-work-logs/${userId}?year=${row.weekStartDate.getFullYear()}&month=${row.weekStartDate.getMonth() + 1}`}
                                            target="_blank"
                                            rel="noopener"
                                            underline="hover"
                                        >
                                            {formatWeekLabel(row.weekStartDate, row.weekEndDate)}
                                        </Link>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {row.runLabel}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{formatDisplayHours(row.scheduleHours, 0)}</TableCell>
                                    <TableCell align="right">{formatDisplayHours(row.holidayHours, 0)}</TableCell>
                                    <TableCell align="right">{formatDisplayHours(row.sickLeaveHours, 0)}</TableCell>
                                    <TableCell align="right">{formatDisplayHours(row.missedHours, 0)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                                        {formatDisplayHours(row.totalHours, 0)}
                                    </TableCell>
                                    <TableCell align="right">{formatDisplayHours(row.extraPaid, 0)}</TableCell>
                                    <TableCell align="right">{formatDisplayHours(row.consideredHours, 0)}</TableCell>
                                    <TableCell align="right">{formatDisplayHours(row.expectedHours, 0)}</TableCell>
                                    <TableCell align="right">{formatSaldo(row.weekSaldo)}</TableCell>
                                    <TableCell align="right">{formatSaldo(row.runningSaldo)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{ "& td": { fontWeight: "bold", borderTop: 2 } }}>
                                <TableCell>Total</TableCell>
                                <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                        Período atual
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">{formatDisplayHours(grandTotal.scheduleHours, 0)}</TableCell>
                                <TableCell align="right">{formatDisplayHours(grandTotal.holidayHours, 0)}</TableCell>
                                <TableCell align="right">{formatDisplayHours(grandTotal.sickLeaveHours, 0)}</TableCell>
                                <TableCell align="right">{formatDisplayHours(grandTotal.missedHours, 0)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "primary.main" }}>
                                    {formatDisplayHours(grandTotal.totalHours, 0)}
                                </TableCell>
                                <TableCell align="right">{formatDisplayHours(grandTotal.extraPaid, 0)}</TableCell>
                                <TableCell align="right">{formatDisplayHours(grandTotal.consideredHours, 0)}</TableCell>
                                <TableCell align="right">{formatDisplayHours(grandTotal.expectedHours, 0)}</TableCell>
                                <TableCell align="right">{formatSaldo(grandTotal.consideredHours - grandTotal.expectedHours)}</TableCell>
                                <TableCell align="right">{formatSaldo(saldoAtual)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: "bold" }}>
                    Saldo atual: {formatSaldo(saldoAtual)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Saldo até {format(toDate, "dd/MM/yyyy")}
                </Typography>
                {isCurrentUserAdmin && latestSnapshot && snapshotCheckpoint && dayOffConfigValid && (
                    <SnapshotManager
                        snapshot={latestSnapshot}
                        recheckWindowStart={recheckWindowStart}
                        checkpoint={snapshotCheckpoint}
                        priorBalanceForRecheck={priorBalanceForRecheck}
                        userId={userId}
                        weeklyWorkHoursSchedule={weeklyWorkHoursSchedule}
                        publicHolidaysSet={publicHolidaysSet}
                        employmentStartDate={anchorDate}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
